import { PageLayout } from '@/layouts/page-layout'
import { useGetAssetCategoriesQuery, useGetAssetTypesQuery, useGetAssetV2Query, useUpdateAssetV2Mutation } from '@/store/services'
import { useEffect, useMemo, useState } from 'react'
import { Button, Input, Typography } from '@material-tailwind/react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { list } from '@/router/utils'
import { Formik, Form } from 'formik'
import { ContractSearcher } from '@/components/contract-searcher'
import { ContractSearcherContext } from '@/components/contract-searcher/context'
import { StatusSwitcher } from '@/components/status-switcher'
import { HasAccess } from '@permify/react-role'

export const AssetV2Edit = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { data: assetResponse, isLoading: assetLoading } = useGetAssetV2Query(id)
  const asset = assetResponse

  const { data: { asset_categories: categories = [] } = {} } = useGetAssetCategoriesQuery()
  const [categoryId, setCategoryId] = useState(null)
  const { data: { asset_types: types = [] } = {} } = useGetAssetTypesQuery(categoryId, { skip: !categoryId })
  const [typeId, setTypeId] = useState(null)
  const [categoryFields, setCategoryFields] = useState({})
  const [typeFields, setTypeFields] = useState({})
  const [status, setStatus] = useState('active')
  const [accredited, setAccredited] = useState('inactive')
  const [submitError, setSubmitError] = useState('')
  const [updateAsset, { isLoading }] = useUpdateAssetV2Mutation()

  useEffect(() => {
    if (asset && asset.asset_type_id) {
      setTypeId(asset.asset_type_id)
      if (asset.asset_type?.asset_category_id) {
        setCategoryId(asset.asset_type.asset_category_id)
      }
      setCategoryFields(asset.category_fields || {})
      setTypeFields(asset.type_fields || {})
      setStatus(asset.status || 'active')
      setAccredited(asset.accredited || 'inactive')
    }
  }, [asset])

  useEffect(() => {
    if (types.length && !typeId && asset) {
      setTypeId(asset.asset_type_id)
    }
  }, [types, asset])

  const selectedType = useMemo(
    () => types.find((t) => t.id === typeId) || asset?.asset_type,
    [types, typeId, asset]
  )

  const fieldsCategory = useMemo(() => {
    const catId = categoryId || asset?.asset_type?.asset_category_id
    const fields = (categories.find((c) => c.id === catId)?.custom_fields || [])
    return fields.filter((f) => !f?.archived)
  }, [categories, categoryId, asset])

  const fieldsType = useMemo(() => (selectedType?.custom_fields || []).filter((f) => !f?.archived), [selectedType])

  const handleFieldChange = (field, value, scope = 'type') => {
    if (scope === 'category') {
      setCategoryFields((prev) => ({ ...prev, [field]: value }))
    } else {
      setTypeFields((prev) => ({ ...prev, [field]: value }))
    }
  }

  if (assetLoading) return <PageLayout title='Editar activo'>Cargando...</PageLayout>
  if (!asset) return <PageLayout title='Editar activo'>No encontrado</PageLayout>

  const rightSection = (
    <>
      <HasAccess permissions='asset_v2#activate'>
        <StatusSwitcher
          label='asset'
          type='status'
          value={status}
          onChange={() => setStatus((prev) => (prev === 'active' ? 'inactive' : 'active'))}
        />
      </HasAccess>
      <HasAccess permissions='asset_v2#accredit'>
        <StatusSwitcher
          label='asset'
          type='accredited'
          value={accredited}
          onChange={() => setAccredited((prev) => (prev === 'active' ? 'inactive' : 'active'))}
        />
      </HasAccess>
    </>
  )

  return (
    <PageLayout title='Editar activo' rightSection={rightSection}>
      <Formik
        enableReinitialize
        initialValues={{
          contractor_contract_id: asset.contractor_contract_id || null,
          identifier_value: asset.identifier_value || ''
        }}
        onSubmit={async (values) => {
          setSubmitError('')
          try {
            await updateAsset({
              id,
              asset_type_id: typeId,
              contractor_contract_id: values.contractor_contract_id,
              identifier_value: values.identifier_value,
              category_fields: categoryFields,
              type_fields: typeFields,
              status,
              accredited
            }).unwrap()
            navigate(list('assets_v2'))
          } catch (err) {
            const msg = err?.data?.errors?.join(', ') || 'Error al actualizar activo'
            setSubmitError(msg)
          }
        }}
      >
        {({ setFieldValue, handleChange, values }) => (
          <Form className='flex flex-col gap-8'>
            {submitError && (
              <div className='text-red-600 bg-red-50 border border-red-200 rounded p-3'>
                {submitError}
              </div>
            )}

            <div className='grid grid-cols-3 gap-8'>
              <div className='col-span-1 flex flex-col gap-2'>
                <Typography variant='h5' className='text-left'>General</Typography>
                <Typography variant='small' className='text-gray-600 text-left'>
                  Rellena la información general del activo.
                </Typography>
              </div>
              <div className='col-span-2 flex flex-col gap-4'>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='col-span-2'>
                    <p className='text-sm font-medium text-gray-700 mb-1'>Contratista / Contrato</p>
                    <div className='grid grid-cols-2 gap-3'>
                      <ContractSearcherContext.Provider value={{ currentContract: asset.contractor_contract }}>
                        <ContractSearcher
                          required
                          name="contractor_contract_id"
                          onChange={(selected) => setFieldValue('contractor_contract_id', selected?.id)}
                        />
                      </ContractSearcherContext.Provider>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='grid grid-cols-3 gap-8'>
              <div className='col-span-1 flex flex-col gap-2'>
                <Typography variant='h5' className='text-left'>Clasificación</Typography>
                <Typography variant='small' className='text-gray-600 text-left'>
                  Clasificación actual del activo (no editable).
                </Typography>
              </div>
              <div className='col-span-2 grid grid-cols-2 gap-4'>
                <div>
                  <label className='text-sm font-medium text-gray-700'>Categoría</label>
                  <Input
                    value={categories.find((c) => c.id === categoryId)?.name || '—'}
                    readOnly
                    className='bg-gray-100'
                  />
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-700'>Tipo</label>
                  <Input
                    value={types.find((t) => t.id === typeId)?.name || selectedType?.name || '—'}
                    readOnly
                    className='bg-gray-100'
                  />
                </div>
              </div>
            </div>

            {fieldsCategory.length > 0 && (
              <div className='grid grid-cols-3 gap-8'>
                <div className='col-span-1 flex flex-col gap-2'></div>
                <div className='col-span-2 bg-gray-100 rounded-2xl p-6 grid grid-cols-2 gap-4'>
                  <div className='col-span-2 flex flex-col gap-1'>
                    <Typography variant='h5' className='text-left'>
                      Datos {categories.find((c) => c.id === categoryId)?.name || 'categoría'}
                    </Typography>
                    <Typography variant='small' className='text-gray-600 text-left'>
                      Rellena los campos de la categoría.
                    </Typography>
                  </div>
                  {fieldsCategory.map((field) => (
                    <Input
                      key={field.key}
                      label={`${field.label}${field.required ? ' *' : ''}`}
                      required={field.required}
                      onChange={(e) => handleFieldChange(field.key, e.target.value, 'category')}
                      value={categoryFields[field.key] ?? ''}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className='grid grid-cols-3 gap-8'>
              <div className='col-span-1 flex flex-col gap-2'></div>
              <div className='col-span-2 bg-gray-100 rounded-2xl p-6 grid grid-cols-2 gap-4'>
                <div className='col-span-2 flex flex-col gap-1'>
                  <Typography variant='h5' className='text-left'>Datos {selectedType?.name || 'tipo'}</Typography>
                  <Typography variant='small' className='text-gray-600 text-left'>
                    Rellena los campos del tipo y el identificador.
                  </Typography>
                </div>
                <Input
                  value={values.identifier_value}
                  onChange={handleChange}
                  name="identifier_value"
                  required
                  label={selectedType?.identifier_label || 'Identificador'}
                />
                {fieldsType.map((field) => (
                  <Input
                    key={field.key}
                    label={`${field.label}${field.required ? ' *' : ''}`}
                    required={field.required}
                    onChange={(e) => handleFieldChange(field.key, e.target.value, 'type')}
                    value={typeFields[field.key] ?? ''}
                  />
                ))}
              </div>
            </div>

            <div className='flex justify-end gap-3 mt-4'>
              <Link to={list('assets_v2')}>
                <Button variant='text'>Cancelar</Button>
              </Link>
              <HasAccess permissions='asset_v2#update'>
                <Button type='submit' loading={isLoading}>Guardar</Button>
              </HasAccess>
            </div>
          </Form>
        )}
      </Formik>
    </PageLayout>
  )
}
